import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 900px;
`;

const StyledTimeline = styled.div`
  position: relative;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 12px;
    bottom: 20px;
    width: 2px;
    background: linear-gradient(to bottom, var(--green) 0%, var(--lightest-navy) 100%);
  }

  @media (max-width: 600px) {
    padding-left: 20px;
  }
`;

const StyledTimelineItem = styled.div`
  position: relative;
  margin-bottom: 56px;

  &:last-of-type {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -34px;
    top: 10px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid var(--green);
    background: var(--navy);
    transition: background 0.2s;
    z-index: 1;
  }

  &:first-of-type::before {
    background: var(--green);
    box-shadow: 0 0 8px var(--green);
  }

  @media (max-width: 600px) {
    &::before {
      left: -26px;
    }
  }

  .item-header {
    margin-bottom: 14px;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px 16px;

    @media (max-width: 600px) {
      flex-direction: column;
      gap: 4px;
    }
  }

  .company-line {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 2px;
  }

  .company-name {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    font-weight: 500;

    a {
      color: var(--green);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .item-location {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);

    &::before {
      content: '— ';
    }
  }

  .item-title {
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;
    margin: 0 0 6px;
    width: 100%;
  }

  .item-range {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin: 0;
  }

  .item-body {
    ul {
      ${({ theme }) => theme.mixins.fancyList};
    }

    p {
      margin: 0 0 10px;
    }

    strong {
      color: var(--lightest-slate);
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;
  const revealContainer = useRef(null);
  const revealItems = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
    revealItems.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I've Worked</h2>

      <StyledTimeline>
        {jobsData &&
          jobsData.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { title, company, url, range, location } = frontmatter;

            return (
              <StyledTimelineItem key={i} ref={el => (revealItems.current[i] = el)}>
                <div className="item-header">
                  <div className="company-line">
                    <span className="company-name">
                      <a href={url} target="_blank" rel="noreferrer">
                        {company}
                      </a>
                    </span>
                    <span className="item-location">{location}</span>
                  </div>
                  <h3 className="item-title">{title}</h3>
                  <p className="item-range">{range}</p>
                </div>

                <div className="item-body" dangerouslySetInnerHTML={{ __html: html }} />
              </StyledTimelineItem>
            );
          })}
      </StyledTimeline>
    </StyledJobsSection>
  );
};

export default Jobs;
